import type { LookupCandidate } from "../schema/lookupModel.js";
import { LOOKUP_BUSY_LABEL, LookupConfirmedMark } from "./LookupAction.js";
import "./styles.css";

/**
 * Phase 524 — THE CANDIDATE LIST: what a lookup shows when the upstream answered with a shortlist
 * instead of a record.
 *
 * <p>It is the {@link LookupResultCard} visual language repeated: the same eyebrow, the same
 * headline, the same labelled `dl` of declared rows — because a candidate and a found record are
 * the same object to the subject, one of them just has not been chosen yet. The rows are the whole
 * point of the list: near-identical names are routine upstream, and the identifier, the status and
 * the address are what let someone tell theirs from the four that look like it.</p>
 *
 * <p>Every option is a real <code>&lt;button&gt;</code>, so the list is reachable by Tab and
 * operable by Enter or Space with nothing to learn; the container is `aria-live="polite"` so a
 * screen-reader user hears that results arrived without the page stealing their place. Picking is
 * NOT submitting and NOT confirming: it fills in the declared fields and collapses the list to the
 * row that was chosen, and the subject may search again or type the value themselves at any point.
 * Nothing here can block the form.</p>
 *
 * <p>Phase 561 — the collapsed picked candidate carries the {@link LookupConfirmedMark} (tick +
 * "Confirmed" in the success tint); an unpicked candidate carries nothing and stays on the neutral
 * card, so chosen and not-yet-chosen never look alike. `aria-pressed` is unchanged.</p>
 */

/** Shown above the list when the declaration did not write its own prompt. */
export const LOOKUP_CHOICE_EYEBROW = "Choose the right one";

/** Shown INSTEAD of the list when the search came back with nothing. */
export const LOOKUP_CHOICE_EMPTY =
  "We could not find a match. Try a different spelling, or fill in the details yourself.";

/**
 * A candidate whose declared `title` path resolved to nothing still has to be pickable and still
 * has to have a name a screen reader can read — its rows are usually what identifies it anyway.
 */
export const LOOKUP_CHOICE_UNTITLED = "This match";

export interface LookupChoiceListProps {
  /** Unique per lookup — the list and its options are ids on the page. */
  id: string;
  /** The declaration's own prompt; absent ⇒ {@link LOOKUP_CHOICE_EYEBROW}. */
  eyebrow?: string;
  candidates: readonly LookupCandidate[];
  /** The index of the candidate already picked, if any — the list collapses to it. */
  chosen?: number;
  onPick: (index: number) => void;
  /**
   * Phase 561 — ONE CARD. When the declaration chains the pick into another lookup
   * (`choose.then_run`), the record that lookup found is shown HERE, inside the collapsed picked
   * candidate, instead of as a second card stacked under another field: the declared `show` rows
   * of the chained lookup, already projected by the page. Rendered only on the picked candidate;
   * absent or empty ⇒ the collapsed candidate is exactly what it was before.
   */
  confirmedRows?: ReadonlyArray<{ label: string; value: string }>;
  /**
   * Phase 561 — the chained lookup is in flight. Its own control is suppressed on a chain, so this
   * is the only place the wait can be shown: the SAME spinner and busy word that control uses.
   */
  confirmedBusy?: boolean;
  /**
   * Phase 561 — the chained lookup's degraded-arm sentence (not found / unavailable / rate
   * limited). The same status line its suppressed control would have carried; the picked
   * candidate stays on screen above it and every answer can still be typed by hand.
   */
  confirmedNotice?: string;
}

export function LookupChoiceList({
  id,
  eyebrow,
  candidates,
  chosen,
  onPick,
  confirmedRows,
  confirmedBusy = false,
  confirmedNotice,
}: LookupChoiceListProps) {
  const prompt = eyebrow?.trim() ? eyebrow.trim() : LOOKUP_CHOICE_EYEBROW;
  const promptId = `${id}-choices-prompt`;
  // Once one is picked the others go: the subject has answered the question the list asked, and a
  // list of rejected candidates left on screen reads as an unfinished decision.
  const shown = candidates
    .map((candidate, index) => ({ candidate, index }))
    .filter((entry) => chosen === undefined || entry.index === chosen);

  return (
    <div className="lookup-choices" aria-live="polite">
      <p id={promptId} className="lookup-result-eyebrow">{prompt}</p>
      {candidates.length === 0 ? (
        <p className="lookup-choices-empty">{LOOKUP_CHOICE_EMPTY}</p>
      ) : (
        <ul className="lookup-choice-list" aria-describedby={promptId}>
          {shown.map(({ candidate, index }) => {
            const picked = chosen === index;
            return (
              <li key={`${index}:${candidate.title}`}>
                <button
                  type="button"
                  id={`${id}-choice-${index}`}
                  className={`lookup-choice${picked ? " lookup-choice-picked" : ""}`}
                  aria-pressed={picked}
                  onClick={() => onPick(index)}
                >
                  {picked && <LookupConfirmedMark />}
                  <span className="lookup-choice-title">{candidate.title || LOOKUP_CHOICE_UNTITLED}</span>
                  {candidate.subtitle && <span className="lookup-choice-subtitle">{candidate.subtitle}</span>}
                  {candidate.rows.length > 0 && (
                    <dl className="lookup-result-grid lookup-choice-grid">
                      {candidate.rows.map((row) => (
                        <div key={row.label} style={{ display: "contents" }}>
                          <dt>{row.label}</dt>
                          <dd>{row.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {picked && confirmedBusy && (
                    <span className="lookup-choice-subtitle lookup-choice-busy">
                      <span className="lookup-spinner" aria-hidden="true" />
                      {LOOKUP_BUSY_LABEL}
                    </span>
                  )}
                  {picked && !confirmedBusy && confirmedRows !== undefined && confirmedRows.length > 0 && (
                    <dl className="lookup-result-grid lookup-choice-grid lookup-choice-confirmed">
                      {confirmedRows.map((row) => (
                        <div key={row.label} style={{ display: "contents" }}>
                          <dt>{row.label}</dt>
                          <dd>{row.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {chosen !== undefined && !confirmedBusy && confirmedNotice && (
        <p className="lookup-status lookup-status-notice" role="status">{confirmedNotice}</p>
      )}
    </div>
  );
}
